import React from 'react';
import { Box, BoxProps, Stack, TextV1 } from '@withjoy/joykit';
import { SkeletonText } from '@shared/components/Skeleton';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { ProductCatalogStorefrontStatus } from '@graphql/generated';
import { brandButtonOverrides, StyledBrandButton, styles } from './ProductTitles.styles';
import { ProductStorefrontStatusBadge } from '../ProductStorefrontStatusBadge';
import { StarRatingWithLabel } from '../ProductRating';
import { CatalogProductItemDetails } from '../../../../Catalog.types';
import { customRounding } from '@shared/utils/numbers';
import { useTranslation } from '@shared/core';

export interface ProductTitlesProps {
  catalogItemId: Maybe<string>;
  title?: string;
  badgeLabel?: string | null;
  summary?: string;
  price?: string | null;
  priceFloatingPointDecimalString?: string;
  priceCurrencyCode?: string;
  salesPrice?: string | null;
  salesPriceCurrenyCode?: string;
  storefrontStatus?: Maybe<ProductCatalogStorefrontStatus>;
  ratingStats: Maybe<NonNullable<CatalogProductItemDetails['rating']>['stats']>;
  /**
   * When provided, the ProductRating component becomes clickable and will scroll to the target element.
   */
  productRatingScrollToElementId?: string;
  onBrandUrlClick?: () => void;
  isFullPrice: boolean;
  itemType: string;
}

export const ProductTitles = (props: ProductTitlesProps) => {
  const {
    title = '',
    summary = '',
    price = '',
    salesPrice = '',
    badgeLabel,
    priceFloatingPointDecimalString,
    salesPriceCurrenyCode,
    priceCurrencyCode,
    productRatingScrollToElementId,
    storefrontStatus,
    onBrandUrlClick,
    ratingStats,
    isFullPrice,
  } = props;

  const { t: translations } = useTranslation('catalogRegistry');
  const { message } = translations('salesPrice');
  const { formatCurrency } = useCurrencyFormatter();

  if (!title && !summary && !price) {
    return null;
  }

  const isNotAvailable = storefrontStatus !== ProductCatalogStorefrontStatus.available;
  const formattedPrice = formatCurrency({
    priceFloatingPointDecimalString: priceFloatingPointDecimalString || '',
    priceCurrencyCode: priceCurrencyCode,
    formatForm: 'short',
  });

  const formattedSalePrice = formatCurrency({
    priceFloatingPointDecimalString: salesPrice || '',
    priceCurrencyCode: salesPriceCurrenyCode,
    formatForm: 'short',
  });
  return (
    <Stack spacing={3}>
      {(summary || title) && (
        <TextV1
          variant="hed4"
          component="h1"
          maxWidth={['100%,null, 400px']}
          overflow="hidden"
        >
          {isNotAvailable && (
            <ProductStorefrontStatusBadge
              storefrontStatus={storefrontStatus}
              variant="head"
              type="title"
            />
          )}
          {ratingStats && (
            <StarRatingWithLabel
              scrollToElementId={productRatingScrollToElementId}
              rating={customRounding(ratingStats.averageRating)}
              totalReviewCount={ratingStats.totalReviewCount}
              marginBottom={5}
              showPartialRating={true}
              iconSize={26}
            />
          )}
          {(title || badgeLabel) && (
            <Box
              marginBottom={3}
              display={'block'}
            >
              {title && (
                <StyledBrandButton
                  variant="link"
                  typographyVariant="hed3"
                  overrides={brandButtonOverrides}
                  cursor={'pointer'}
                  marginRight={6}
                  onClick={onBrandUrlClick}
                >
                  {title}
                </StyledBrandButton>
              )}
              {badgeLabel && (
                <Box
                  as="span"
                  color="purple6"
                  verticalAlign="middle"
                >
                  {badgeLabel}
                </Box>
              )}
            </Box>
          )}
          {summary}
        </TextV1>
      )}
      {priceFloatingPointDecimalString && priceCurrencyCode && (
        <TextV1
          variant="hed4"
          component="p"
        >
          <Box
            as="span"
            textDecoration={!isFullPrice ? 'line-through' : ''}
            __css={styles.price(storefrontStatus === ProductCatalogStorefrontStatus.discontinued)}
          >
            {formattedPrice}
          </Box>
          {!isFullPrice && (
            <Box
              as="span"
              color="#D80B0B"
            >
              {message()} {formattedSalePrice}
            </Box>
          )}
        </TextV1>
      )}
    </Stack>
  );
};

ProductTitles.Skeleton = (props: BoxProps) => {
  return (
    <Box
      width="100%"
      {...props}
    >
      <SkeletonText
        width="45%"
        rows={1}
        skeletonHeight="16px"
      />
      <SkeletonText
        width="100%"
        rows={1}
        skeletonHeight="26px"
        marginTop={5}
      />
      <SkeletonText
        width="40%"
        rows={1}
        skeletonHeight="26px"
        marginTop={5}
      />
    </Box>
  );
};
